import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';

const colorClasses: Record<string, { activeBg: string; activeText: string; tagBg: string; tagText: string }> = {
  primary: { activeBg: 'bg-primary-500', activeText: 'text-background-50', tagBg: 'bg-primary-100', tagText: 'text-primary-700' },
  accent: { activeBg: 'bg-accent-500', activeText: 'text-background-50', tagBg: 'bg-accent-100', tagText: 'text-accent-700' },
  secondary: { activeBg: 'bg-secondary-500', activeText: 'text-background-50', tagBg: 'bg-secondary-100', tagText: 'text-secondary-700' },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const filteredArticles = articles.filter((a) => a.category === slug);
  const colors = category ? colorClasses[category.color] || colorClasses.primary : colorClasses.primary;

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <h1 className="text-2xl font-bold text-foreground-950 mb-4">分类未找到</h1>
        <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">返回首页</Link>
      </div>
    );
  }

  return (
    <>
      <SEOMeta
        title={`${category.name} - 吃瓜网`}
        description={category.description}
        keywords={`${category.name},吃瓜网,娱乐八卦,${category.name}最新消息`}
        canonicalUrl={`https://chigua.com/category/${category.slug}`}
      />
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${colors.tagBg} ${colors.tagText}`}>
              <i className={`${category.icon} text-lg`}></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              {category.name}
            </h1>
          </div>
          <p className="text-foreground-500 text-sm md:text-base">{category.description}</p>
        </div>

        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 flex-wrap">
          {categories.map((cat) => {
            const isActive = cat.slug === slug;
            const catColors = colorClasses[cat.color] || colorClasses.primary;
            return (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  isActive
                    ? `${catColors.activeBg} ${catColors.activeText}`
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            );
          })}
        </div>

        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors duration-200 mb-2">
                    {article.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span>{article.author}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span>{article.publishDate}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-inbox-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类下暂无文章</p>
          </div>
        )}
      </div>
    </>
  );
}